/**
 * Confirmation dialog for removing a POC row from the Order Assignment Configuration
 * table (local draft until the page Save action persists).
 */

import { useEffect, useState } from "react";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";

const DELETE_POC_CONFIG_ROW_CONFIRM_ID = "delete-poc-config-row-confirm";

interface DeletePocConfigRowConfirmProps {
  /** POC display name shown in the message (same column as the table “POC” field). */
  pocLabel: string | null;
  /** Stable row id passed to removeRow on confirm. */
  rowId: string | null;
  /** Invoked when the user confirms; should remove the row from working state. */
  onConfirm: (rowId: string) => void;
  /** Clears pending delete state when the modal closes or after confirm. */
  onClose: () => void;
}

/**
 * Mirrors the Form Matrix delete confirm flow: opens when `rowId` is set, confirms removal.
 *
 * @param pocLabel - Shown in the body copy.
 * @param rowId - Row identifier to pass to `onConfirm`.
 * @param onConfirm - Removes the row from in-memory table (caller persists via Save).
 * @param onClose - Resets parent pending-delete state.
 */
const DeletePocConfigRowConfirm = ({
  pocLabel,
  rowId,
  onConfirm,
  onClose,
}: DeletePocConfigRowConfirmProps) => {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (rowId) {
      openModal(DELETE_POC_CONFIG_ROW_CONFIRM_ID);
    }
  }, [rowId]);

  const handleConfirm = () => {
    if (!rowId) return;
    setBusy(true);
    try {
      onConfirm(rowId);
    } finally {
      setBusy(false);
      closeModal(DELETE_POC_CONFIG_ROW_CONFIRM_ID);
      onClose();
    }
  };

  const handleCancel = () => {
    if (busy) return;
    closeModal(DELETE_POC_CONFIG_ROW_CONFIRM_ID);
    onClose();
  };

  if (!rowId) {
    return null;
  }

  const label = pocLabel?.trim() ? pocLabel.trim() : "This POC";

  return (
    <Modal
      dialogId={DELETE_POC_CONFIG_ROW_CONFIRM_ID}
      title="Remove POC row"
      saveButtonText={busy ? "Removing…" : "Yes, remove"}
      cancelText="Cancel"
      onSave={handleConfirm}
      onCancel={handleCancel}
      onClose={handleCancel}
      disableSave={busy}
      autoCloseOnSave={false}
    >
      <div className="flex flex-col items-center justify-center gap-3 py-2">
        <div className="text-center text-sm text-primaryGray-3">
          Remove <span className="font-semibold">{label}</span> from this table?
          Click <span className="font-semibold">Save</span> at the bottom to
          persist changes to Firestore.
        </div>
      </div>
    </Modal>
  );
};

export default DeletePocConfigRowConfirm;
